import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FaBars, FaHome, FaUsers, FaCalendar, FaUser, FaCog, FaComment } from 'react-icons/fa';
import { IoMdPeople } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../types/redux';
import NavBarLogo from '../misc/NavBarLogo';
import Avatar from '../misc/Avatar';
import ConfirmLogoutModal from '../modals/ConfirmLogoutModal';
import useAuth from '../../hooks/useAuth';
import { authenticationService, profileService } from '../../services';
import { Language } from '../../types/requests';
import useLanguage from '../../hooks/useLanguage';
import { setLanguage } from '../../redux/slices/applicationSlice';

interface AppLayoutProps {
  children?: React.ReactNode;
  contentWrapper?: boolean;
}

export default function AppLayout({ children, contentWrapper = true }: AppLayoutProps): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useAuth();
  const lang = useLanguage();
  const [closed, setClosed] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const profilePicture = useSelector((s: RootState) => s.application.profilePicture || '');
  const displayName = auth
    ? auth.firstName
      ? `${auth.firstName} ${auth.lastName || ''}`.trim()
      : (auth.alias || '').replace('+', ' ')
    : '';

  const general = [
    { path: '/', icon: FaHome, label: t('general.home') },
    { path: '/communities', icon: IoMdPeople, label: t('general.communities') },
    { path: '/connections', icon: FaUsers, label: t('general.connections') },
    { path: '/agenda', icon: FaCalendar, label: t('general.agenda') },
    { path: '/chats', icon: FaComment, label: t('general.chats') },
  ];

  const personalItems = [
    { path: '/profile', icon: FaUser, label: t('general.profile') },
    { path: '/settings', icon: FaCog, label: t('general.settings') },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/communities') return location.pathname.startsWith('/communit'); // covers communities and community subpaths
    return location.pathname.startsWith(path);
  };

  const logout = () => {
    authenticationService.logOut();
    navigate('/login');
  };

  useEffect(() => {
    if (auth) {
      profileService.getProfilePicture().catch(() => {});
    }
  }, [auth]);

  useEffect(() => setClosed(true), [location.pathname]);

  return (
    <div className="flex h-screen w-full bg-secondary-50">
      {/* Left Sidebar */}
      <div className="hidden w-[260px] flex-shrink-0 border-r border-details-500/50 bg-secondary lg:flex lg:flex-col">
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-details-500/50 px-4">
          <NavBarLogo />
        </div>

        {/* Navigation Sections */}
        <div className="flex flex-1 flex-col overflow-y-auto py-4">
          <div className="flex-1 px-3">
            <div className="mb-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.general')}</h3>
              <nav className="space-y-1">
                {general.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={classNames(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        {
                          'bg-secondary-200 text-primary': isActive,
                          'border-transparent text-details/50 hover:bg-secondary-50': !isActive,
                        },
                      )}>
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="mb-4 border-t border-details-500/50 pt-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.personal')}</h3>
              <nav className="space-y-1">
                {personalItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={classNames(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        {
                          'bg-secondary-200 text-primary': isActive,
                          'border-transparent text-details/50 hover:bg-secondary-50': !isActive,
                        },
                      )}>
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Other Section - Always at Bottom */}
          <div className="px-3 pt-4">
            <div className="border-t border-details-500/50 pt-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.other')}</h3>
              <nav className="space-y-1">
                <Link
                  to="/feedback"
                  className={classNames(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    {
                      'bg-secondary-200 text-primary': isActivePath('/feedback'),
                      'text-details/50 hover:bg-secondary-50': !isActivePath('/feedback'),
                    },
                  )}>
                  <span className="truncate">{t('general.feedback')}</span>
                </Link>
                {auth && (
                  <button
                    type="button"
                    onClick={() => setShowLogoutModal(true)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-error hover:bg-secondary-50">
                    <span className="truncate">{t('auth.logout')}</span>
                  </button>
                )}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="fixed left-0 right-0 top-0 z-50 flex h-16 items-center justify-between border-b border-details-500/50 bg-secondary px-4 lg:hidden">
        <FaBars
          className="h-6 w-6 cursor-pointer text-primary"
          onClick={() => setClosed(!closed)}
          aria-label="Open menu"
        />
        <NavBarLogo />
      </div>

      {/* Mobile Menu & Overlay */}
      {!closed && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setClosed(true)} />}

      <div
        className={classNames(
          'fixed bottom-0 left-0 top-16 z-50 w-[260px] flex-shrink-0 border-r border-details-500/50 bg-secondary transition-transform lg:hidden',
          {
            '-translate-x-full': closed,
            'translate-x-0': !closed,
          },
        )}>
        <div className="flex flex-1 flex-col overflow-y-auto py-4">
          <div className="flex-1 px-3">
            <div className="mb-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.general')}</h3>
              <nav className="space-y-1">
                {general.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setClosed(true)}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-details/50 hover:bg-secondary-50">
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="mb-4 border-t border-details-500/50 pt-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.personal')}</h3>
              <nav className="space-y-1">
                {personalItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setClosed(true)}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-details/50 hover:bg-secondary-50">
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="px-3 pt-4">
            <div className="border-t border-details-500/50 pt-4">
              <h3 className="mb-2 text-xs font-semibold tracking-wider text-details/50">{t('general.other')}</h3>
              <nav className="space-y-1">
                <Link
                  to="/feedback"
                  onClick={() => setClosed(true)}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-details/50 hover:bg-secondary-50">
                  <span className="truncate">{t('general.feedback')}</span>
                </Link>
                {auth && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowLogoutModal(true);
                      setClosed(true);
                    }}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-error hover:bg-secondary-50">
                    <span className="truncate">{t('auth.logout')}</span>
                  </button>
                )}
                {/* Mobile language switch */}
                <button
                  type="button"
                  onClick={() => {
                    const newLang = lang === Language.EN ? Language.NL : Language.EN;
                    dispatch(setLanguage(newLang));
                    setClosed(true);
                  }}
                  className="mt-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary-300 hover:bg-secondary-50">
                  <img src={`/images/flags/${lang}.svg`} alt={lang} className="h-5 w-5" />
                  <span>{lang.toUpperCase()}</span>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden pt-16 lg:pt-0">
        {/* Top Header Bar (hidden on small screens) */}
        <div className="hidden h-16 items-center justify-between border-b border-details-500/50 bg-secondary px-6 lg:flex">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                const newLang = lang === Language.EN ? Language.NL : Language.EN;
                dispatch(setLanguage(newLang));
              }}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary-300 hover:bg-secondary-50">
              <img src={`/images/flags/${lang}.svg`} alt={lang} className="h-5 w-5" />
              <span>{lang.toUpperCase()}</span>
            </button>
          </div>
          {auth && (
            <button type="button" onClick={() => navigate('/profile')} className="flex items-center gap-3">
              <Avatar alias={auth.alias} src={profilePicture} className="h-10 w-10" />
              <span className="text-sm font-medium text-primary">{displayName}</span>
            </button>
          )}
        </div>

        {/* Page Content */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-secondary-50">
          {contentWrapper ? (
            <div className="p-0 lg:p-6">
              <div className="min-h-[calc(100vh-4rem)] w-full rounded-none bg-secondary lg:mx-auto lg:min-h-full lg:w-auto lg:rounded-lg lg:p-6">
                {children || <Outlet />}
              </div>
            </div>
          ) : (
            <div className="min-h-[calc(100vh-4rem)] w-full lg:p-0">{children || <Outlet />}</div>
          )}
        </div>
      </div>
      <ConfirmLogoutModal
        open={showLogoutModal}
        setOpen={setShowLogoutModal}
        onConfirm={() => {
          logout();
          setShowLogoutModal(false);
        }}
      />
    </div>
  );
}
